import type { AgentApiCandidateFormAnswersRecord } from '@/types/serializers';
import type { StatusTab } from './FormReviewTab';

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}`;
}

interface Props {
  statusTab: StatusTab;
  answers: AgentApiCandidateFormAnswersRecord[];
  isLoading?: boolean;
  error?: Error | null;
  onRowClick?: (answerId: number) => void;
}

export default function FormReviewTable({ answers, isLoading, error, onRowClick }: Props) {
  const isEmpty = answers.length === 0;

  if (error) {
    return (
      <div className="mt-md py-12 text-center text-body-sm text-sub">
        回答の読み込みに失敗しました。ページをリロードしてください。
      </div>
    );
  }

  if (isLoading) {
    return <div className="mt-md py-12 text-center text-body-sm text-sub">読み込み中...</div>;
  }

  return (
    <div className="mt-md overflow-hidden rounded-sm border border-solid border-border shadow-layer1">
      <table className="w-full border-collapse">
        <thead className="bg-gray_darken">
          <tr className="border-b border-solid border-border">
            <th className="h-[42px] w-[300px] px-md text-left align-middle text-heading-xxs text-main">
              氏名
            </th>
            <th className="h-[42px] px-md text-left align-middle text-heading-xxs text-main">
              送信日時
            </th>
          </tr>
        </thead>
        <tbody>
          {isEmpty ? (
            <tr>
              <td colSpan={2} className="px-md py-12 text-center text-body-sm text-sub">
                回答がありません
              </td>
            </tr>
          ) : (
            answers.map((row) => (
              <tr
                key={row.id}
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                className={`border-b border-solid border-border ${
                  onRowClick
                    ? 'cursor-pointer hover:bg-gray-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary'
                    : ''
                }`}
                onClick={() => onRowClick?.(row.id)}
                onKeyDown={(e) => {
                  if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onRowClick(row.id);
                  }
                }}
              >
                <td className="h-[56px] px-md align-middle text-body-sm text-main">
                  {row.candidate_profile_name ?? '—'}
                </td>
                <td className="h-[56px] px-md align-middle text-body-sm text-main">
                  {formatDateTime(row.created_at)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
